import { useMutation, useQuery } from '@apollo/client';
import { Switch } from '@newfront-insurance/core-ui';
import { Button, cn } from '@newfront-insurance/core-ui/v2';
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Pencil,
  PlayCircle,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ArchiveNovaScheduleDialog } from './archive-nova-schedule-dialog';
import { NovaScheduleEditor } from './nova-schedule-editor';
import {
  formatNovaScheduleDateTimeShort,
  formatNovaScheduleDelivery,
  formatNovaScheduleRunDate,
  formatNovaScheduleStatus,
  groupNovaScheduleRunsByMonth,
  isSuccessfulNovaScheduleRun,
  toNovaScheduleRecurrenceInput,
} from './nova-schedule-model';
import {
  formatNovaScheduleRecurrence,
  NO_ACCOUNT_SELECTED_SCHEDULE_LABEL,
} from '../../../../lib/nova-schedule-proposal';
import {
  ARCHIVE_NOVA_SCHEDULE,
  GET_NOVA_SCHEDULE_RUNS,
  GET_NOVA_SCHEDULES,
  PAUSE_NOVA_SCHEDULE,
  RESUME_NOVA_SCHEDULE,
  RUN_NOVA_SCHEDULE_NOW,
  UPDATE_NOVA_SCHEDULE,
  isNovaScheduleRecord,
  type NovaScheduleRunRecord,
  type UpdateNovaScheduleCommand,
} from '../../../../queries/nova-schedule-management';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { SettingsActionButton } from '../settings-shared/settings-action-button';

export function NovaScheduleDetailView({ scheduleUuid, onBack }: { scheduleUuid: string; onBack: () => void }) {
  const { userUuid, setActiveChat, openChatAtMessage } = useChatOverlayContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const scheduleQueryVariables = {
    filters: { uuids: [scheduleUuid], ownerUserUuids: [userUuid] },
  };
  const runsQueryVariables = { filters: { scheduleUuids: [scheduleUuid] } };
  const scheduleQuery = useQuery(GET_NOVA_SCHEDULES, {
    variables: scheduleQueryVariables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const runsQuery = useQuery(GET_NOVA_SCHEDULE_RUNS, {
    variables: runsQueryVariables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const [updateSchedule, updateState] = useMutation(UPDATE_NOVA_SCHEDULE);
  const [pauseSchedule, pauseState] = useMutation(PAUSE_NOVA_SCHEDULE);
  const [resumeSchedule, resumeState] = useMutation(RESUME_NOVA_SCHEDULE);
  const [archiveSchedule, archiveState] = useMutation(ARCHIVE_NOVA_SCHEDULE);
  const [runScheduleNow, runNowState] = useMutation(RUN_NOVA_SCHEDULE_NOW);
  const queriedSchedule = scheduleQuery.data?.getNovaSchedules[0];
  const schedule = queriedSchedule && isNovaScheduleRecord(queriedSchedule) ? queriedSchedule : undefined;
  const runs = runsQuery.data?.getNovaScheduleRuns ?? [];
  const hasPendingRuns = runs.some((run) => run.status === 'CLAIMED' || run.status === 'RUNNING');
  const { startPolling, stopPolling } = runsQuery;

  useEffect(() => {
    if (hasPendingRuns) startPolling(1_000);
    else stopPolling();
    return stopPolling;
  }, [hasPendingRuns, startPolling, stopPolling]);

  if (!schedule && scheduleQuery.loading) {
    return (
      <div className="text-steel-400 flex flex-1 items-center justify-center text-sm">
        <Loader2 aria-hidden="true" className="mr-2 size-5 animate-spin" />
        Loading schedule...
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className="flex flex-1 items-center justify-center px-8">
        <div className="border-steel-75 max-w-sm rounded-[4px] border bg-white p-5 text-center">
          <p className="text-steel-500 text-[14px] leading-5">This schedule is no longer available.</p>
          <Button type="button" variant="secondary" className="mt-4" onClick={onBack}>
            Back to scheduled
          </Button>
        </div>
      </div>
    );
  }

  const refreshSchedule = async () => {
    await scheduleQuery.refetch();
  };

  const handleSave = async (command: UpdateNovaScheduleCommand) => {
    try {
      const result = await updateSchedule({ variables: { uuid: scheduleUuid, command } });
      if (result.errors?.length || !result.data?.updateNovaSchedule) return;
      // The mutation has persisted the draft. Clear its exit guard immediately,
      // even when the subsequent refresh is slow or fails.
      setIsEditing(false);
      await refreshSchedule();
    } catch {
      // Failed saves keep the editor open. Refresh errors cannot undo a successful save.
    }
  };

  if (isEditing) {
    return (
      <NovaScheduleEditor
        schedule={schedule}
        isSaving={updateState.loading}
        error={updateState.error}
        onCancel={() => setIsEditing(false)}
        onSave={handleSave}
      />
    );
  }

  const isActive = schedule.status.status === 'ACTIVE';
  const canRunNow = isActive || schedule.status.status === 'PAUSED';
  const isStatusPending = pauseState.loading || resumeState.loading;
  const runGroups = groupNovaScheduleRunsByMonth(runs, schedule.timezone);

  const handleToggle = async () => {
    try {
      const mutation = isActive ? pauseSchedule : resumeSchedule;
      await mutation({ variables: { uuid: scheduleUuid } });
      await refreshSchedule();
    } catch {
      // The mutation state renders the error without leaking an unhandled rejection.
    }
  };

  const handleArchive = async () => {
    try {
      const result = await archiveSchedule({ variables: { uuid: scheduleUuid } });
      if (!result.data?.archiveNovaSchedule) return;
      setIsArchiveDialogOpen(false);
      onBack();
    } catch {
      // Keep the dialog open so the user can retry the failed archive.
    }
  };

  const handleRunNow = async () => {
    try {
      await runScheduleNow({ variables: { uuid: scheduleUuid } });
      await runsQuery.refetch();
      await refreshSchedule();
    } catch {
      // The mutation state renders the error and leaves the action retryable.
    }
  };

  const viewRunInChat = (run: NovaScheduleRunRecord) => {
    const chatUuid = run.chatUuid;
    const messageUuid = run.messageUuid;
    if (!chatUuid) return;
    if (messageUuid) openChatAtMessage(chatUuid, messageUuid);
    else setActiveChat(chatUuid);
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-white px-8 pt-10 pb-8 [scrollbar-gutter:stable_both-edges]">
      <div className="mx-auto flex w-[736px] max-w-full flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <button
            type="button"
            onClick={onBack}
            className="text-brand-400 flex min-w-0 items-center gap-1 text-[17px] leading-6 font-semibold"
          >
            <ArrowLeft aria-hidden="true" className="size-4 shrink-0" />
            <span className="truncate">
              Scheduled <span className="text-steel-100">/</span>{' '}
              <span className="text-steel-500 font-normal">{schedule.title}</span>
            </span>
          </button>
          <div className="flex shrink-0 items-center gap-3">
            <SettingsActionButton
              label={`Delete ${schedule.title}`}
              tooltip="Delete schedule"
              onClick={() => setIsArchiveDialogOpen(true)}
            >
              <Trash2 aria-hidden="true" className="size-4" />
            </SettingsActionButton>
            <SettingsActionButton
              label={`Edit ${schedule.title}`}
              tooltip="Edit schedule"
              onClick={() => setIsEditing(true)}
            >
              <Pencil aria-hidden="true" className="size-4" />
            </SettingsActionButton>
            {schedule.status.status !== 'COMPLETED' ? (
              <div className="flex items-center gap-2">
                <Switch
                  enabled={isActive}
                  locked={isStatusPending}
                  onClick={() => void handleToggle()}
                  testId="nova-schedule-status-switch"
                />
                <span
                  className={cn(
                    'min-w-[58px] text-[14px] leading-5 font-semibold',
                    isActive ? 'text-[#1C907C]' : 'text-[#FF4773]',
                  )}
                >
                  {isStatusPending ? 'Saving...' : formatNovaScheduleStatus(schedule.status.status)}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        <section className="border-steel-75 flex flex-col gap-4 rounded-[4px] border bg-white p-[15px]">
          <h2 className="text-steel-500 text-[14px] leading-5 font-semibold">{schedule.title}</h2>
          <ExpandableSchedulePrompt prompt={schedule.prompt} />
          <dl className="grid grid-cols-[64px_minmax(0,1fr)] gap-x-5 gap-y-4">
            <ScheduleDetail label="When">
              {formatNovaScheduleRecurrence(toNovaScheduleRecurrenceInput(schedule.recurrence))}
            </ScheduleDetail>
            <ScheduleDetail label="Account">
              {schedule.account?.name ?? NO_ACCOUNT_SELECTED_SCHEDULE_LABEL}
            </ScheduleDetail>
            <ScheduleDetail label="Next run">
              {formatNovaScheduleDateTimeShort(schedule.nextRunAt, schedule.timezone)}
            </ScheduleDetail>
            <ScheduleDetail label="Delivery">{formatNovaScheduleDelivery(schedule)}</ScheduleDetail>
          </dl>
        </section>

        <div className="flex items-center justify-between gap-4">
          <h2 className="text-steel-400 text-[12px] leading-5 font-semibold tracking-[1.2px] uppercase">
            Recent deliveries
          </h2>
          <span title={canRunNow ? undefined : 'Completed schedules cannot be run'}>
            <Button
              type="button"
              variant="secondary"
              size="small"
              loading={runNowState.loading}
              disabled={!canRunNow}
              onClick={() => void handleRunNow()}
              aria-busy={runNowState.loading}
              data-testid="nova-schedule-run-now-button"
            >
              <PlayCircle aria-hidden="true" className="mr-2 size-4" />
              Run now
            </Button>
          </span>
        </div>

        {runsQuery.loading && runsQuery.data === undefined ? (
          <div className="text-steel-400 flex items-center justify-center py-12 text-sm">
            <Loader2 aria-hidden="true" className="mr-2 size-5 animate-spin" />
            Loading deliveries...
          </div>
        ) : runsQuery.error ? (
          <div className="border-steel-75 rounded-[4px] border p-4">
            <p className="text-[13px] text-[#C5342A]">Unable to load recent deliveries.</p>
            <Button type="button" variant="secondary" className="mt-3" onClick={() => void runsQuery.refetch()}>
              Retry
            </Button>
          </div>
        ) : runs.length === 0 ? (
          <EmptyScheduleRuns nextRunAt={schedule.nextRunAt} timezone={schedule.timezone} />
        ) : (
          <div className="border-steel-75 overflow-hidden rounded-[4px] border bg-white">
            {runGroups.map((group) => (
              <section key={group.key}>
                <div className="bg-steel-50 border-steel-75 flex items-center justify-between border-b px-[14px] py-[10px]">
                  <h3 className="text-steel-500 text-[12px] leading-4 font-semibold">{group.label}</h3>
                  <span className="text-steel-400 text-[12px] leading-4">
                    {group.runs.length} {group.runs.length === 1 ? 'run' : 'runs'}
                  </span>
                </div>
                <div className="divide-steel-75 divide-y">
                  {group.runs.map((run) => (
                    <ScheduleRunRow
                      key={run.uuid}
                      run={run}
                      timezone={schedule.timezone}
                      onView={() => viewRunInChat(run)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {pauseState.error || resumeState.error ? (
          <p className="text-[13px] text-[#C5342A]">Unable to change the schedule status. Try again.</p>
        ) : null}
        {runNowState.error ? <p className="text-[13px] text-[#C5342A]">Unable to run this schedule.</p> : null}
      </div>

      <ArchiveNovaScheduleDialog
        open={isArchiveDialogOpen}
        scheduleTitle={schedule.title}
        isArchiving={archiveState.loading}
        error={archiveState.error}
        onOpenChange={setIsArchiveDialogOpen}
        onConfirm={() => void handleArchive()}
      />
    </div>
  );
}

function ScheduleDetail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-steel-500 text-[12px] leading-4 font-semibold">{label}</dt>
      <dd className="text-steel-400 min-w-0 text-[14px] leading-5">{children}</dd>
    </>
  );
}

function ExpandableSchedulePrompt({ prompt }: { prompt: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);
  const promptRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const element = promptRef.current;
    if (!element || isExpanded) return;

    const observer = new ResizeObserver(() => setIsExpandable(element.scrollHeight > element.clientHeight));
    observer.observe(element);
    return () => observer.disconnect();
  }, [isExpanded, prompt]);

  return (
    <div
      data-testid="nova-schedule-prompt"
      className={cn(
        'border-steel-50 bg-steel-50 text-steel-400 flex flex-col rounded-[6px] border p-[10px]',
        isExpandable && !isExpanded && 'h-[150px]',
      )}
    >
      <p ref={promptRef} className={cn('text-[14px] leading-5', !isExpanded && 'line-clamp-5')}>
        {prompt}
      </p>
      {isExpandable ? (
        <button
          type="button"
          onClick={() => setIsExpanded((current) => !current)}
          className="text-brand-400 mt-auto flex w-fit items-center gap-1 text-[14px] leading-6 font-semibold"
        >
          {isExpanded ? 'Show less' : 'Show more'}
          {isExpanded ? (
            <ChevronUp aria-hidden="true" className="size-4" />
          ) : (
            <ChevronDown aria-hidden="true" className="size-4" />
          )}
        </button>
      ) : null}
    </div>
  );
}

function ScheduleRunRow({
  run,
  timezone,
  onView,
}: {
  run: NovaScheduleRunRecord;
  timezone: string;
  onView: () => void;
}) {
  const isPending = run.status === 'CLAIMED' || run.status === 'RUNNING';
  const canView = Boolean(run.chatUuid) && isSuccessfulNovaScheduleRun(run.status);

  return (
    <div className="flex items-center gap-3 px-[14px] py-[10px]">
      {isPending ? (
        <Loader2 aria-label="Running" className="text-brand-400 size-5 animate-spin" />
      ) : isSuccessfulNovaScheduleRun(run.status) ? (
        <CheckCircle2 aria-label="Succeeded" className="size-5 text-[#1CB779]" />
      ) : (
        <XCircle aria-label={formatRunStatus(run.status)} className="size-5 text-[#FF4773]" />
      )}
      <span className="text-steel-500 min-w-0 flex-1 text-[12px] leading-4">
        {formatNovaScheduleRunDate(run.occurrenceAt, timezone)}
      </span>
      <span className="text-steel-400 text-[12px] leading-4 font-semibold">{formatRunStatus(run.status)}</span>
      <button
        type="button"
        disabled={!canView}
        onClick={onView}
        className="text-brand-400 text-[12px] leading-4 font-semibold disabled:text-[#BEC7D0]"
      >
        View in chat
      </button>
    </div>
  );
}

function formatRunStatus(status: NovaScheduleRunRecord['status']): string {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

function EmptyScheduleRuns({ nextRunAt, timezone }: { nextRunAt: string | null; timezone: string }) {
  return (
    <div className="border-steel-75 flex min-h-[316px] flex-col items-center justify-center gap-3 rounded-[4px] border bg-white p-8 text-center">
      <CalendarDays aria-hidden="true" className="text-steel-500 size-6" />
      <span className="text-steel-400 text-[12px] leading-5 font-semibold tracking-[1.2px] uppercase">
        Your first delivery
      </span>
      <span className="text-steel-500 text-[17px] leading-6">
        {formatNovaScheduleDateTimeShort(nextRunAt, timezone)}
      </span>
    </div>
  );
}
